import { SLIDE_THUMBNAIL_MODE } from '@app/shared'
import type { SlideContent } from '@segnavia/format'

/**
 * Holds a stage slide's ticket while its frame is mounted. `retain` and
 * `release` take the exact content object the stage mounted, and a release
 * forgets only that object, so the release of a replaced frame leaves its
 * successor's ticket in place.
 */
export class SlideTicketMemo<T> {
  private readonly tickets = new Map<
    string,
    { version: number; ready: Promise<T>; content?: T }
  >()

  resolve(id: string, version: number, mint: () => Promise<T>): Promise<T> {
    const held = this.tickets.get(id)
    if (held?.version === version) return held.ready

    const ready = mint().catch((reason: unknown) => {
      if (this.tickets.get(id)?.ready === ready) this.tickets.delete(id)
      throw reason
    })
    this.tickets.set(id, { version, ready })
    return ready
  }

  private readonly generations = new Map<string, number>()

  /** Moves with every forget, so a slide's next ticket is new content to the
   * stage and replaces the frame that failed rather than matching it. */
  generation(id: string): number {
    return this.generations.get(id) ?? 0
  }

  forget(id: string): void {
    this.tickets.delete(id)
    this.generations.set(id, this.generation(id) + 1)
  }

  retain(id: string, content: T): void {
    const held = this.tickets.get(id)
    if (!held) return
    this.tickets.set(id, {
      version: held.version,
      ready: Promise.resolve(content),
      content,
    })
  }

  release(id: string, content: T): void {
    if (this.tickets.get(id)?.content === content) this.tickets.delete(id)
  }
}

/**
 * Shares one mint among the resolutions that ask while it is in flight, and
 * mints afresh once it settles: the frame that loads a ticket spends it, and a
 * thumbnail scrolled back into view loads again.
 */
export class PendingTicketMemo<T> {
  private readonly pending = new Map<string, Promise<T>>()

  resolve(id: string, version: number, mint: () => Promise<T>): Promise<T> {
    const key = `${id}:${version}`
    const held = this.pending.get(key)
    if (held) return held

    const ready = mint().finally(() => {
      if (this.pending.get(key) === ready) this.pending.delete(key)
    })
    this.pending.set(key, ready)
    return ready
  }
}

export function thumbnailSlideContent(url: string): SlideContent {
  const separator = url.includes('?') ? '&' : '?'
  return {
    kind: 'url',
    src: `${url}${separator}for=${SLIDE_THUMBNAIL_MODE}`,
  }
}
